/**
 * メンバー管理ページの戦闘力セクション (組織フィルタのスコープ別表示)。
 *
 * - TeamBudgetChart + Qサマリー (実績 vs 目標) を上部に表示 (Qサマリーは全フィルタで表示)。
 * - 全体ビュー: 戦闘力サマリーを PDG / ACE 左右並びで表示。
 * - PDG TE ビュー: Qサマリーの下に戦闘力サマリーの PDG 部分 + 3 つのカスタムビュー。
 * - TE サブチームビュー (Growth / Experience): Qサマリーの下に戦闘力サマリーを
 *   nick_name 名簿で絞った 1 テーブルで表示。カスタムビューは TE 全体の括りなので出さない。
 * - ACE ビュー: Qサマリーの下に戦闘力サマリーを 4 チーム別 (代表色見出し) で表示。
 *
 * members ページ本体 (routes/members/index.tsx) の肥大化を避けるため、戦闘力の
 * 表示分岐をこのコンポーネントに切り出している。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー管理ページの戦闘力サマリー表示 (全体 / PDG TE / TEサブチーム(Growth・Experience) / ACE スコープ別)
 * @graph-connects none
 */

import { isTetSubTeam } from '@cortex/domain-product-innovation-lab-types';
import type { CombatPowerResponse } from '../../../types/combat-power.js';
import type { MemberOrganization } from '../../../types/api.js';
import { CombatPowerBlock } from './CombatPowerBlock.js';
import { TeamBudgetChart } from './TeamBudgetChart.js';
import { QuarterSummaryCard } from './QuarterSummaryCard.js';
import type { QuarterSummaryScope } from './QuarterSummaryCard.js';
import { CombatPowerCustomViews } from './CombatPowerCustomViews.js';

interface MembersCombatPowerSectionProps {
  data: CombatPowerResponse;
  /** 選択中の組織フィルタ (undefined = 全体) */
  selectedOrg: MemberOrganization | undefined;
  /** Qサマリーカードの集計スコープ */
  quarterSummaryScope: QuarterSummaryScope;
  /** name(lower) → group/team マップ */
  memberTeamMap: Map<string, { group: string; team: string }>;
  /** 表示中の月インデックス */
  currentMonthIdx: number;
  /** 月切替ハンドラ */
  onMonthChange: (idx: number) => void;
  /** Qシート切替ハンドラ */
  onSheetChange: (sheet: string) => void;
  /** 担当者名 (小文字) → email */
  nameToEmail: Map<string, string>;
}

/** @graph-connects none */
export function MembersCombatPowerSection({
  data,
  selectedOrg,
  quarterSummaryScope,
  memberTeamMap,
  currentMonthIdx,
  onMonthChange,
  onSheetChange,
  nameToEmail,
}: MembersCombatPowerSectionProps) {
  return (
    <>
      {/* Team Budget Chart + Qサマリー（幅比 約5:2）。Qサマリーは全フィルタで表示する */}
      <div className="flex flex-col xl:flex-row gap-6 items-stretch">
        <div className="min-w-0 xl:flex-[5]">
          <TeamBudgetChart
            data={data}
            selectedOrg={selectedOrg}
            memberTeamMap={memberTeamMap}
            currentMonthIdx={currentMonthIdx}
          />
        </div>
        <div className="min-w-0 xl:flex-[2]">
          <QuarterSummaryCard
            data={data}
            memberTeamMap={memberTeamMap}
            scope={quarterSummaryScope}
          />
        </div>
      </div>

      {/* 全体ビュー: PDG / ACE を左右並びで表示 */}
      {!selectedOrg && (
        <CombatPowerBlock
          data={data}
          selectedMonthIdx={currentMonthIdx}
          onMonthChange={onMonthChange}
          onSheetChange={onSheetChange}
          nameToEmail={nameToEmail}
        />
      )}

      {/* PDG TE ビュー: 全体サマリー (PDG) を左、カスタムビューを右に左右並びで表示 (Qサマリーの下) */}
      {selectedOrg === 'tet' && (
        <div className="flex flex-col xl:flex-row gap-6 items-start">
          <div className="min-w-0 w-full xl:flex-1">
            <CombatPowerBlock
              data={data}
              selectedMonthIdx={currentMonthIdx}
              onMonthChange={onMonthChange}
              onSheetChange={onSheetChange}
              nameToEmail={nameToEmail}
              scope="pdg"
            />
          </div>
          <div className="min-w-0 w-full xl:flex-1">
            <CombatPowerCustomViews
              data={data}
              memberTeamMap={memberTeamMap}
              currentMonthIdx={currentMonthIdx}
              nameToEmail={nameToEmail}
            />
          </div>
        </div>
      )}

      {/* TE サブチームビュー: 戦闘力サマリーを名簿で絞った 1 テーブルで表示 (Qサマリーの下)。
          カスタムビューは TE 全体の括りなのでサブチームには出さない */}
      {selectedOrg !== undefined && isTetSubTeam(selectedOrg) && (
        <CombatPowerBlock
          data={data}
          selectedMonthIdx={currentMonthIdx}
          onMonthChange={onMonthChange}
          onSheetChange={onSheetChange}
          nameToEmail={nameToEmail}
          scope={selectedOrg}
        />
      )}

      {/* ACE ビュー: 戦闘力サマリーを 4 チーム別 (代表色見出し) に表示 (Qサマリーの下) */}
      {selectedOrg === 'ace' && (
        <CombatPowerBlock
          data={data}
          selectedMonthIdx={currentMonthIdx}
          onMonthChange={onMonthChange}
          onSheetChange={onSheetChange}
          nameToEmail={nameToEmail}
          scope="ace"
        />
      )}
    </>
  );
}
